import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { Add, Delete, Edit, AssignmentInd } from "@mui/icons-material";
import {
  getVouchers,
  createVoucher,
  updateVoucher,
  deleteVoucher,
  assignVoucher,
  getUsersByVoucher,
  updateUserVoucher,
  revokeUserVoucher,
} from "../../api/vouchersApi";

const TYPE_OPTIONS = [
  { value: "percent", label: "Giảm theo % (Percent)" },
  { value: "fixed", label: "Giảm số tiền (Fixed)" },
  { value: "freeship", label: "Miễn phí ship (FreeShip)" },
];

function toInputDateTimeLocal(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function toIsoOrNull(dtLocal) {
  if (!dtLocal) return null;
  const d = new Date(dtLocal);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const emptyVoucher = {
  voucherId: 0,
  code: "",
  label: "",
  type: "percent",
  value: 0,
  minOrder: null,
  maxDiscount: null,
  startAt: null,
  endAt: null,
  isActive: true,
  globalUsageLimit: null,
  perUserUsageLimit: null,
  stackable: false,
};

export default function VouchersPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  // dialog tạo/sửa
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyVoucher });

  // dialog cấp voucher
  const [openAssign, setOpenAssign] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm, setAssignForm] = useState({
    userId: "",
    remainingUses: 1,
    status: "active",
    validFrom: "",
    validTo: "",
    source: "admin",
    note: "",
  });

  // ===== USERS OF A VOUCHER =====
  const [openVoucherUsers, setOpenVoucherUsers] = useState(false);
  const [voucherUsers, setVoucherUsers] = useState([]);
  const [voucherUserLoading, setVoucherUserLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const [openUvEdit, setOpenUvEdit] = useState(false);
  const [editingUv, setEditingUv] = useState(null);
  const [uvForm, setUvForm] = useState({
    remainingUses: 1,
    status: "active",
    is_active: true,
    validFrom: "",
    validTo: "",
    note: "",
  });

  const [selectedUvIds, setSelectedUvIds] = useState([]);

  // toast
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return rows;
    return rows.filter((x) => {
      const a = (x.code || "").toLowerCase();
      const b = (x.label || "").toLowerCase();
      return a.includes(k) || b.includes(k);
    });
  }, [rows, q]);

  async function reload() {
    setLoading(true);
    try {
      const data = await getVouchers();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        message: "Tải danh sách voucher thất bại.",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyVoucher });
    setOpenForm(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({
      voucherId: row.voucherId,
      code: row.code ?? "",
      label: row.label ?? "",
      type: row.type ?? "percent",
      value: row.value ?? 0,
      minOrder: row.minOrder ?? null,
      maxDiscount: row.maxDiscount ?? null,
      startAt: row.startAt ?? null,
      endAt: row.endAt ?? null,
      isActive: row.isActive ?? true,
      globalUsageLimit: row.globalUsageLimit ?? null,
      perUserUsageLimit: row.perUserUsageLimit ?? null,
      stackable: row.stackable ?? false,
    });
    setOpenForm(true);
  }

  async function submitForm() {
    const payload = {
      code: (form.code || "").trim(),
      label: form.label,
      type: form.type,
      value: Number(form.value || 0),
      minOrder:
        form.minOrder === "" || form.minOrder === null
          ? null
          : Number(form.minOrder),
      maxDiscount:
        form.maxDiscount === "" || form.maxDiscount === null
          ? null
          : Number(form.maxDiscount),
      startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
      endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
      isActive: !!form.isActive,
      globalUsageLimit:
        form.globalUsageLimit === "" || form.globalUsageLimit === null
          ? null
          : Number(form.globalUsageLimit),
      perUserUsageLimit:
        form.perUserUsageLimit === "" || form.perUserUsageLimit === null
          ? null
          : Number(form.perUserUsageLimit),
      stackable: !!form.stackable,
    };

    // validate nhẹ
    if (!payload.code) {
      setToast({
        open: true,
        type: "error",
        message: "Vui lòng nhập mã (Code).",
      });
      return;
    }
    if (payload.value < 0) {
      setToast({
        open: true,
        type: "error",
        message: "Giá trị giảm không hợp lệ.",
      });
      return;
    }

    try {
      setLoading(true);
      if (!editing) {
        await createVoucher(payload);
        setToast({
          open: true,
          type: "success",
          message: "Tạo voucher thành công.",
        });
      } else {
        await updateVoucher(editing.voucherId, payload);
        setToast({
          open: true,
          type: "success",
          message: "Cập nhật voucher thành công.",
        });
      }
      setOpenForm(false);
      await reload();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        message:
          e?.response?.data?.message || e?.message || "Lưu voucher thất bại.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function removeVoucher(row) {
    const ok = window.confirm(
      `Xoá voucher "${row.code}"?\nHành động không thể hoàn tác.`,
    );
    if (!ok) return;

    try {
      setLoading(true);
      await deleteVoucher(row.voucherId);
      setToast({
        open: true,
        type: "success",
        message: "Xoá voucher thành công.",
      });
      await reload();
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        message:
          e?.response?.data?.message || e?.message || "Xoá voucher thất bại.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function openVoucherUsersDialog(voucher) {
    try {
      setSelectedVoucher(voucher);
      setSelectedUvIds([]); // ✅ BẮT BUỘC
      setVoucherUserLoading(true);
      const data = await getUsersByVoucher(voucher.voucherId);
      setVoucherUsers(Array.isArray(data) ? data : []);
      setOpenVoucherUsers(true);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        message: "Không tải được danh sách user của voucher.",
      });
    } finally {
      setVoucherUserLoading(false);
    }
  }

  function openAssignDialog(row) {
    setAssignTarget(row);
    setAssignForm({
      userId: "",
      remainingUses: 1,
      status: "active",
      validFrom: "",
      validTo: "",
      source: "admin",
      note: "",
    });
    setOpenAssign(true);
  }

  async function submitAssign() {
    if (!assignTarget) return;

    const userId = Number(assignForm.userId);
    if (!userId || userId <= 0) {
      setToast({ open: true, type: "error", message: "UserId không hợp lệ." });
      return;
    }

    const payload = {
      userId,
      voucherId: assignTarget.voucherId,
      remainingUses:
        assignForm.remainingUses === "" || assignForm.remainingUses == null
          ? 1
          : Number(assignForm.remainingUses),
      status: (assignForm.status || "active").trim(),
      validFrom: toIsoOrNull(assignForm.validFrom),
      validTo: toIsoOrNull(assignForm.validTo),
      source: (assignForm.source || "admin").trim(),
      note: assignForm.note || "",
    };

    try {
      setLoading(true);
      await assignVoucher(payload);
      setToast({
        open: true,
        type: "success",
        message: `Đã cấp voucher ${assignTarget.code} cho user #${userId}.`,
      });
      setOpenAssign(false);
    } catch (e) {
      console.error(e);
      setToast({
        open: true,
        type: "error",
        message:
          e?.response?.data?.message || e?.message || "Cấp voucher thất bại.",
      });
    } finally {
      setLoading(false);
    }
  }

  function openEditUserVoucher(row) {
    setEditingUv(row);
    setUvForm({
      remainingUses: row.remainingUses ?? 1,
      status: row.status ?? "active",
      is_active: !!row.isActive,
      validFrom: toInputDateTimeLocal(row.validFrom),
      validTo: toInputDateTimeLocal(row.validTo),
      note: row.note ?? "",
    });
    setOpenUvEdit(true);
  }

  async function submitUpdateUserVoucher() {
    try {
      await updateUserVoucher(editingUv.userVoucherId, {
        RemainingUses: uvForm.remainingUses,
        Status: uvForm.status,
        is_active: uvForm.is_active,
        ValidFrom: toIsoOrNull(uvForm.validFrom),
        ValidTo: toIsoOrNull(uvForm.validTo),
        Note: uvForm.note,
      });

      setToast({
        open: true,
        type: "success",
        message: "Cập nhật voucher cho user thành công",
      });

      setOpenUvEdit(false);
      await openVoucherUsersDialog(selectedVoucher);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Cập nhật voucher thất bại",
      });
    }
  }

  async function handleRevokeUserVoucher(row) {
    if (!window.confirm("Thu hồi voucher của user này?")) return;
    await revokeUserVoucher(row.userVoucherId);
    await openVoucherUsersDialog(selectedVoucher);
  }

  async function handleBulkRevoke() {
    if (!selectedUvIds.length) return;
    if (!window.confirm("Thu hồi tất cả voucher đã chọn?")) return;

    try {
      setVoucherUserLoading(true);

      const results = await Promise.allSettled(
        selectedUvIds.map((id) => revokeUserVoucher(id)),
      );

      const failed = results.filter((r) => r.status === "rejected").length;

      if (failed > 0) {
        setToast({
          open: true,
          type: "error",
          message: `Thu hồi xong nhưng lỗi ${failed} user`,
        });
      } else {
        setToast({
          open: true,
          type: "success",
          message: "Thu hồi voucher thành công",
        });
      }

      setSelectedUvIds([]);
      await openVoucherUsersDialog(selectedVoucher);
    } finally {
      setVoucherUserLoading(false);
    }
  }

  function isVoucherOutOfStock(voucher) {
    if (voucher.globalUsageLimit == null) return false;
    return voucher.assignedCount >= voucher.globalUsageLimit;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Quản lý Voucher
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tạo / Sửa / Xoá voucher + Cấp voucher cho người dùng
          </Typography>
        </Box>

        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Tìm theo mã (code) hoặc tên (label)..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Thêm voucher
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{ border: "1px solid #eee", borderRadius: 2, overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={600}>Danh sách voucher</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            {loading && <CircularProgress size={18} />}
            <Button variant="text" onClick={reload}>
              Tải lại
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                {[
                  "ID",
                  "Mã",
                  "Tên",
                  "Loại",
                  "Giá trị",
                  "Đơn tối thiểu",
                  "Giảm tối đa",
                  "Bật/Tắt",
                  "Cộng dồn",
                  "Giới hạn/user",
                  "Giới hạn/toàn hệ thống",
                  "Bắt đầu",
                  "Kết thúc",
                  "Thao tác",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      fontSize: 13,
                      borderBottom: "1px solid #eee",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => (
                <tr key={r.voucherId}>
                  <td style={td}>{r.voucherId}</td>
                  <td style={td}>{r.code}</td>
                  <td style={td}>{r.label}</td>
                  <td style={td}>{r.type}</td>
                  <td style={td}>
                    {Number(r.value || 0).toLocaleString("vi-VN")}
                  </td>
                  <td style={td}>
                    {r.minOrder != null
                      ? Number(r.minOrder).toLocaleString("vi-VN")
                      : "-"}
                  </td>
                  <td style={td}>
                    {r.maxDiscount != null
                      ? Number(r.maxDiscount).toLocaleString("vi-VN")
                      : "-"}
                  </td>
                  <td style={td}>{r.isActive ? "BẬT" : "TẮT"}</td>
                  <td style={td}>{r.stackable ? "CÓ" : "KHÔNG"}</td>
                  <td style={td}>{r.perUserUsageLimit ?? "-"}</td>
                  <td style={td}>{r.globalUsageLimit ?? "-"}</td>
                  <td style={td}>
                    {r.startAt ? new Date(r.startAt).toLocaleString() : "-"}
                  </td>
                  <td style={td}>
                    {r.endAt ? new Date(r.endAt).toLocaleString() : "-"}
                  </td>

                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {/* <Tooltip title="Cấp voucher cho user">
                      <IconButton
                        onClick={() => openAssignDialog(r)}
                        size="small"
                      >
                        <AssignmentInd fontSize="small" />
                      </IconButton>
                    </Tooltip> */}

                    <Tooltip
                      title={
                        r.globalUsageLimit != null &&
                        r.assignedCount >= r.globalUsageLimit
                          ? "Voucher đã hết lượt"
                          : "Cấp voucher cho user"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={
                            r.globalUsageLimit != null &&
                            r.assignedCount >= r.globalUsageLimit
                          }
                          onClick={() => openAssignDialog(r)}
                        >
                          <AssignmentInd fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Xem user đã được cấp">
                      <IconButton
                        onClick={() => openVoucherUsersDialog(r)}
                        size="small"
                      >
                        👥
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Chỉnh sửa voucher">
                      <IconButton onClick={() => openEdit(r)} size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Xoá voucher">
                      <IconButton onClick={() => removeVoucher(r)} size="small">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}

              {!filtered.length && (
                <tr>
                  <td style={{ ...td, padding: 20 }} colSpan={14}>
                    Không có voucher nào.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      {/* DIALOG TẠO/SỬA */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editing ? `Sửa voucher #${editing.voucherId}` : "Thêm voucher"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Mã voucher (Code)"
                value={form.code}
                onChange={(e) =>
                  setForm((s) => ({ ...s, code: e.target.value }))
                }
                fullWidth
                helperText="VD: TET2026, FREESHIP50..."
              />
              <TextField
                label="Tên hiển thị (Label)"
                value={form.label}
                onChange={(e) =>
                  setForm((s) => ({ ...s, label: e.target.value }))
                }
                fullWidth
                helperText="VD: Tết Sale 20%, FreeShip..."
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label="Loại voucher"
                value={form.type}
                onChange={(e) =>
                  setForm((s) => ({ ...s, type: e.target.value }))
                }
                fullWidth
              >
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Giá trị giảm"
                type="number"
                value={form.value}
                onChange={(e) =>
                  setForm((s) => ({ ...s, value: e.target.value }))
                }
                fullWidth
                helperText={
                  form.type === "percent"
                    ? "Nhập % (VD: 10, 20...)"
                    : form.type === "fixed"
                      ? "Nhập số tiền (VD: 50000)"
                      : "Với freeship: có thể dùng Value/MaxDiscount để giới hạn"
                }
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Đơn tối thiểu (MinOrder)"
                type="number"
                value={form.minOrder ?? ""}
                onChange={(e) =>
                  setForm((s) => ({ ...s, minOrder: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Giảm tối đa (MaxDiscount)"
                type="number"
                value={form.maxDiscount ?? ""}
                onChange={(e) =>
                  setForm((s) => ({ ...s, maxDiscount: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Bắt đầu (StartAt)"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={toInputDateTimeLocal(form.startAt)}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    startAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Kết thúc (EndAt)"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={toInputDateTimeLocal(form.endAt)}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    endAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Giới hạn mỗi user (PerUserUsageLimit)"
                type="number"
                value={form.perUserUsageLimit ?? ""}
                onChange={(e) =>
                  setForm((s) => ({ ...s, perUserUsageLimit: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Giới hạn toàn hệ thống (GlobalUsageLimit)"
                type="number"
                value={form.globalUsageLimit ?? ""}
                onChange={(e) =>
                  setForm((s) => ({ ...s, globalUsageLimit: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, isActive: e.target.checked }))
                    }
                  />
                }
                label="Kích hoạt (Active)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.stackable}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, stackable: e.target.checked }))
                    }
                  />
                }
                label="Cho phép cộng dồn (Stackable)"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Đóng</Button>
          <Button variant="contained" onClick={submitForm} disabled={loading}>
            Lưu voucher
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CẤP VOUCHER */}
      <Dialog
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cấp voucher: <b>{assignTarget?.code}</b>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="UserId cần cấp"
              value={assignForm.userId}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, userId: e.target.value }))
              }
              type="number"
              fullWidth
              helperText="Nhập UserId (ví dụ: 123)"
            />
            <TextField
              label="Số lượt còn lại (RemainingUses)"
              value={assignForm.remainingUses}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, remainingUses: e.target.value }))
              }
              type="number"
              fullWidth
            />
            <TextField
              label="Trạng thái"
              value={assignForm.status}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, status: e.target.value }))
              }
              fullWidth
              helperText="Gợi ý: active / used / disabled"
            />
            <TextField
              label="Có hiệu lực từ"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={assignForm.validFrom}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, validFrom: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Hết hiệu lực lúc"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={assignForm.validTo}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, validTo: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Nguồn cấp (Source)"
              value={assignForm.source}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, source: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Ghi chú"
              value={assignForm.note}
              onChange={(e) =>
                setAssignForm((s) => ({ ...s, note: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssign(false)}>Đóng</Button>
          <Button variant="contained" onClick={submitAssign} disabled={loading}>
            Cấp voucher
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUvEdit} onClose={() => setOpenUvEdit(false)}>
        <DialogTitle>Sửa voucher của user</DialogTitle>
        <DialogContent>
          <TextField
            label="RemainingUses"
            type="number"
            value={uvForm.remainingUses}
            onChange={(e) =>
              setUvForm((s) => ({ ...s, remainingUses: e.target.value }))
            }
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={uvForm.is_active}
                onChange={(e) =>
                  setUvForm((s) => ({ ...s, is_active: e.target.checked }))
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUvEdit(false)}>Đóng</Button>
          <Button variant="contained" onClick={submitUpdateUserVoucher}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openVoucherUsers}
        onClose={() => setOpenVoucherUsers(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            User đã được cấp voucher: <b>{selectedVoucher?.code}</b> (
            {voucherUsers.length} user)
          </span>

          <Button
            color="error"
            variant="contained"
            size="small"
            disabled={!selectedUvIds.length || voucherUserLoading}
            onClick={handleBulkRevoke}
          >
            Thu hồi đã chọn ({selectedUvIds.length})
          </Button>
        </DialogTitle>

        <DialogContent>
          {voucherUserLoading ? (
            <CircularProgress />
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      checked={
                        voucherUsers.length > 0 &&
                        selectedUvIds.length === voucherUsers.length
                      }
                      indeterminate={
                        selectedUvIds.length > 0 &&
                        selectedUvIds.length < voucherUsers.length
                      }
                      onChange={(e) =>
                        setSelectedUvIds(
                          e.target.checked
                            ? voucherUsers.map((x) => x.userVoucherId)
                            : [],
                        )
                      }
                    />
                  </th>
                  <th>UserId</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Active</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {voucherUsers.map((uv) => (
                  <tr
                    key={uv.userVoucherId}
                    style={{
                      opacity: uv.isActive ? 1 : 0.5,
                      background: uv.isActive ? "transparent" : "#fafafa",
                    }}
                  >
                    <td>
                      <Checkbox
                        checked={selectedUvIds.includes(uv.userVoucherId)}
                        onChange={(e) =>
                          setSelectedUvIds((prev) =>
                            e.target.checked
                              ? [...prev, uv.userVoucherId]
                              : prev.filter((id) => id !== uv.userVoucherId),
                          )
                        }
                      />
                    </td>
                    <td>{uv.userId}</td>
                    <td>{uv.username}</td>
                    <td>{uv.email}</td>
                    <td>{uv.remainingUses}</td>
                    <td>{uv.status}</td>
                    <td>{uv.isActive ? "BẬT" : "TẮT"}</td>
                    <td>
                      <Button
                        size="small"
                        onClick={() => openEditUserVoucher(uv)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRevokeUserVoucher(uv)}
                      >
                        Thu hồi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={toast.type}
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const td = {
  padding: "10px 12px",
  borderBottom: "1px solid #eee",
  fontSize: 13,
  verticalAlign: "top",
};
